import {
  memo,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
} from "react";
import { MoreHorizontalIcon, MoreVerticalIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { MenuItem } from "./MenuItem";
import {
  type MenuItems as MenuItemsType,
  MenuItemType,
} from "./MenuItems.type";

type Props = {
  items: MenuItemsType[][];
  variant?: "horizontal" | "vertical";
  hideIcon?: boolean;
  name: string;
};

/**
 * @deprecated This component is deprecated and will be removed in a future version.
 * Please use ActionMenu instead.
 */
export const DropdownMenu = memo(
  ({
    children,
    items,
    variant = "horizontal",
    hideIcon,
    name,
  }: PropsWithChildren<Props>) => {
    const isOpen = useBoolean(false);

    const handleToggleMenu = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        isOpen.toggle();
      },
      [isOpen],
    );

    return (
      <Popover onOpenChange={isOpen.setValue} open={isOpen.value}>
        <PopoverTrigger asChild>
          <div className="flex items-center justify-between w-full">
            {children}
            {!hideIcon ? (
              <ThemedIconButton
                ariaLabel="Open menu"
                className={cnTwMerge({
                  "bg-icon-button/no-background/pressed": isOpen.value,
                })}
                dataTestId={`${name}-dropdown-menu-button`}
                icon={
                  variant === "horizontal" ? (
                    <MoreVerticalIcon
                      className={cnTwMerge("defaultTransition", {
                        "rotate-90": isOpen.value,
                      })}
                    />
                  ) : (
                    <MoreHorizontalIcon
                      className={cnTwMerge("defaultTransition", {
                        "rotate-90": isOpen.value,
                      })}
                    />
                  )
                }
                onClick={handleToggleMenu}
                variant="noBackground"
              />
            ) : null}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="rounded-companyBorderRadius bg-dark-gray p-3 mb-3 w-[228px]"
          data-testid="dropdown-menu-popover"
          side="right"
          sideOffset={32}
        >
          {items.map((menuGroup) => (
            <div className="py-0.5" key={menuGroup[0].title}>
              {menuGroup.map((item) => (
                <div className="py-1" key={item.title}>
                  {item.type === MenuItemType.Button ? (
                    <ThemedButton
                      dataTestId={`${name}-dropdown-menu-item-${item.title}`}
                      fullWidth
                      onClick={item.action}
                      variant="destructive"
                    >
                      {item.title}
                    </ThemedButton>
                  ) : (
                    <MenuItem
                      action={item.action}
                      data-testid={`${name}-dropdown-menu-item-${item.title}`}
                      href={item.href}
                      icon={item.icon}
                      text={item.title}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </PopoverContent>
      </Popover>
    );
  },
);
